import * as Sentry from "@sentry/react"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "design-system/atoms/Accordion"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import { Popover, PopoverContent, PopoverTrigger } from "design-system/atoms/Popover"
import { Separator } from "design-system/atoms/Separator"
import { Textarea } from "design-system/atoms/Textarea"
import { Card } from "design-system/molecules/Card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { MarkdownRenderer } from "design-system/organisms/chat/MarkdownRenderer"
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { cn } from "design-system/utils"
import { renderLiquid } from "lopermut"
import { FlaskConical, List } from "lucide-react"
import { useTestGorgiasTicketMutation } from "modules/settings/hooks/gorgias/useTestGorgiasTicketMutation"
import { useEffect, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { WorkflowNodeSchema, CreateGorgiasTicketConfigSchema, GorgiasPriorityEnum } from "schema"
import { toast } from "sonner"
import z from "zod"
import { useWorkflowBuilder } from "../../hooks/useWorkflowBuilder"
import { useEventPayload } from "../../hooks/useEventPayload"

type TicketPreviewProps = {
  formValues: z.infer<typeof CreateGorgiasTicketConfigSchema>
  eventPayload: Record<string, unknown>
}

function TicketPreview({ formValues, eventPayload }: TicketPreviewProps) {
  const { t } = useTranslation()

  const customerValues = {
    name: {
      copy: t("campaigns.action.availableActions.CreateGorgiasTicket.config.customer.name"),
      value: t(
        "campaigns.action.availableActions.CreateGorgiasTicket.config.customer.namePlaceholder",
      ),
    },
    email: {
      copy: t("campaigns.action.availableActions.CreateGorgiasTicket.config.customer.email"),
      value: t(
        "campaigns.action.availableActions.CreateGorgiasTicket.config.customer.emailPlaceholder",
      ),
    },
    phone: {
      copy: t("campaigns.action.availableActions.CreateGorgiasTicket.config.customer.phone"),
      value: t(
        "campaigns.action.availableActions.CreateGorgiasTicket.config.customer.phonePlaceholder",
      ),
    },
  }

  const values = {
    subject: {
      field: "subject",
      copy: t("campaigns.action.availableActions.CreateGorgiasTicket.config.subject"),
      value: formValues.subject,
      showValue: true,
    },
    body: {
      field: "body",
      copy: t("campaigns.action.availableActions.CreateGorgiasTicket.config.body"),
      value: formValues.body,
      showValue: true,
    },
    priority: {
      field: "priority",
      copy: t("campaigns.action.availableActions.CreateGorgiasTicket.config.priority"),
      value: formValues.priority,
      showValue: false,
    },
    tags: {
      field: "tags",
      copy: t("campaigns.action.availableActions.CreateGorgiasTicket.config.tags"),
      value: formValues.tags,
      showValue: true,
    },
  }

  return (
    <Card className="flex flex-col gap-2">
      <Accordion type="single" collapsible className="w-full text-left">
        <AccordionItem value="preview">
          <AccordionTrigger>
            {t("campaigns.action.availableActions.CreateGorgiasTicket.config.preview")}
          </AccordionTrigger>
          <AccordionContent className="flex flex-col gap-3">
            <div className="flex flex-col gap-0">
              <span className="text-sm text-muted-foreground">
                {t("campaigns.action.availableActions.CreateGorgiasTicket.config.customer.title")}
              </span>
              {Object.entries(customerValues).map(([key, { copy, value }]) => (
                <div key={key} className="flex flex-row gap-0 items-center text-sm">
                  <span>{copy}</span>
                  <span>{":"}&nbsp;</span>
                  <span>{value}</span>
                </div>
              ))}
            </div>
            {Object.entries(values).map(([key, { copy, field, value, showValue }]) => {
              let displayedValue: string | number
              if (key === "priority") {
                displayedValue = t(
                  `campaigns.action.availableActions.CreateGorgiasTicket.config.priorities.${value ?? "none"}`,
                )
              } else {
                try {
                  displayedValue = renderLiquid(value ? String(value) : "", eventPayload)
                } catch (error) {
                  Sentry.captureException(error)
                  displayedValue = ""
                }
              }

              return (
                <div key={key} className="flex flex-col gap-0">
                  <span className="text-sm text-muted-foreground">{copy}</span>
                  <span
                    className={cn(
                      "text-sm",
                      showValue && "font-semibold",
                      field === "customFields" &&
                        "bg-muted p-2 font-mono rounded-md whitespace-pre-wrap",
                    )}
                  >
                    {displayedValue && displayedValue.toString().length > 0 ? displayedValue : "-"}
                  </span>
                </div>
              )
            })}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>
  )
}

type CreateGorgiasTicketConfigurationProps = {
  nodeIndex: number
  node: Extract<z.infer<typeof WorkflowNodeSchema>, { type: "Action" }>
}

export function CreateGorgiasTicketConfiguration({
  nodeIndex,
  node,
}: CreateGorgiasTicketConfigurationProps) {
  const [testDialogOpen, setTestDialogOpen] = useState(false)

  const { t } = useTranslation()
  const { form } = useWorkflowBuilder()
  const { eventPayload, eventName } = useEventPayload({ nodeId: node.id })

  const { mutateAsync: testGorgiasTicket, isPending: isTestGorgiasTicketPending } =
    useTestGorgiasTicketMutation()

  async function handleTestZendeskTicket() {
    const formConfig = form.getValues(`nodes.${nodeIndex}.config`)
    const result = CreateGorgiasTicketConfigSchema.safeParse(formConfig)

    if (!result.success) {
      toast.error(
        t("campaigns.action.availableActions.CreateGorgiasTicket.config.test.notCompletedFields"),
      )
      return
    }

    await testGorgiasTicket({
      data: formConfig as z.input<typeof CreateGorgiasTicketConfigSchema>,
      eventPayload: eventPayload ?? {},
    })

    toast.success(t("campaigns.action.availableActions.CreateGorgiasTicket.config.test.success"))
    setTestDialogOpen(false)
  }

  useEffect(() => {
    const result = CreateGorgiasTicketConfigSchema.safeParse(node.config)

    if (!result.success) {
      form.setValue(`nodes.${nodeIndex}.config`, {
        subject: "",
        comment: null,
        brandId: null,
        ticketFormId: null,
        customFields: null,
        tags: null,
        priority: null,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const subject = form.watch(`nodes.${nodeIndex}.config.subject`) || ""
  const body = form.watch(`nodes.${nodeIndex}.config.body`) || null
  const priority = form.watch(`nodes.${nodeIndex}.config.priority`) || null
  const tags = form.watch(`nodes.${nodeIndex}.config.tags`) || null

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2 justify-start items-start">
        <span className="text-sm text-muted-foreground">
          <Trans
            i18nKey="campaigns.action.availableActions.CreateGorgiasTicket.config.description"
            components={{
              b: <span className="font-medium" />,
              a: (
                <a
                  href="https://shopify.dev/docs/api/liquid"
                  target="_blank"
                  rel="noreferrer"
                  className="underline hover:opacity-80"
                />
              ),
            }}
          />
        </span>
      </div>
      <TicketPreview
        formValues={{ subject, body, priority, tags }}
        eventPayload={eventPayload ?? {}}
      />
      <div className="flex flex-row gap-2 justify-between w-full">
        <Button
          variant="testing"
          disabled={!form.formState.isValid}
          onClick={() => setTestDialogOpen(true)}
          className="flex-1"
        >
          <FlaskConical className="size-4" />
          <span>{t("campaigns.action.availableActions.CreateGorgiasTicket.config.test.cta")}</span>
        </Button>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="flex-1">
              <List className="size-4" />
              {t("campaigns.action.availableActions.CreateGorgiasTicket.config.data")}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="flex flex-col gap-2">
            <span className="text-base">
              <Trans
                i18nKey="campaigns.action.availableActions.CreateGorgiasTicket.config.availableData"
                components={{ b: <span className="font-medium" /> }}
                values={{ eventName }}
              />
            </span>
            <MarkdownRenderer
              className="bg-muted rounded-md overflow-auto max-h-96 border"
              codeClassName="border-none"
            >{`\`\`\`json\n${JSON.stringify(eventPayload, null, 2)}\n\`\`\``}</MarkdownRenderer>
          </PopoverContent>
        </Popover>
      </div>
      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent>
          <DialogTitle>
            {t("campaigns.action.availableActions.CreateGorgiasTicket.config.test.title")}
          </DialogTitle>
          <DialogDescription>
            {t("campaigns.action.availableActions.CreateGorgiasTicket.config.test.warning")}
          </DialogDescription>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setTestDialogOpen(false)}
              disabled={isTestGorgiasTicketPending}
            >
              {t("shared.cancel")}
            </Button>
            <Button onClick={handleTestZendeskTicket} disabled={isTestGorgiasTicketPending}>
              {t("shared.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Separator />
      <FormField
        control={form.control}
        name={`nodes.${nodeIndex}.config.subject`}
        render={() => (
          <FormItem>
            <FormLabel>
              {t("campaigns.action.availableActions.CreateGorgiasTicket.config.subject")}
              <span className="text-destructive">{" *"}</span>
            </FormLabel>
            <FormControl>
              <Input
                value={subject}
                onChange={(e) => {
                  form.setValue(`nodes.${nodeIndex}.config.subject`, e.target.value, {
                    shouldDirty: true,
                  })
                  form.trigger(`nodes.${nodeIndex}.config`)
                }}
                placeholder={t(
                  "campaigns.action.availableActions.CreateGorgiasTicket.config.subjectPlaceholder",
                )}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name={`nodes.${nodeIndex}.config.body`}
        render={() => (
          <FormItem>
            <FormLabel>
              {t("campaigns.action.availableActions.CreateGorgiasTicket.config.body")}
            </FormLabel>
            <FormControl>
              <Textarea
                value={body}
                onChange={(e) => {
                  form.setValue(`nodes.${nodeIndex}.config.body`, e.target.value, {
                    shouldDirty: true,
                  })
                  form.trigger(`nodes.${nodeIndex}.config`)
                }}
                onKeyDown={(e) => {
                  // Allow Enter key for line breaks, prevent form submission
                  if (e.key === "Enter") {
                    e.stopPropagation()
                  }
                }}
                placeholder={t(
                  "campaigns.action.availableActions.CreateGorgiasTicket.config.bodyPlaceholder",
                )}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name={`nodes.${nodeIndex}.config.priority`}
        render={() => (
          <FormItem>
            <FormLabel>
              {t("campaigns.action.availableActions.CreateGorgiasTicket.config.priority")}
              <span className="text-destructive">{" *"}</span>
            </FormLabel>
            <FormControl>
              <Select
                value={priority ?? "none"}
                onValueChange={(value) => {
                  const newValue = value === "none" ? null : value
                  form.setValue(`nodes.${nodeIndex}.config.priority`, newValue, {
                    shouldDirty: true,
                  })
                  form.trigger(`nodes.${nodeIndex}.config`)
                }}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={t(
                      "campaigns.action.availableActions.CreateGorgiasTicket.config.priorities.none",
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" className="text-muted-foreground">
                    {t(
                      "campaigns.action.availableActions.CreateGorgiasTicket.config.priorities.none",
                    )}
                  </SelectItem>
                  {Object.values(GorgiasPriorityEnum).map((priority) => (
                    <SelectItem key={priority} value={priority}>
                      {t(
                        `campaigns.action.availableActions.CreateGorgiasTicket.config.priorities.${priority}`,
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name={`nodes.${nodeIndex}.config.tags`}
        render={() => (
          <FormItem>
            <FormLabel>
              {t("campaigns.action.availableActions.CreateGorgiasTicket.config.tags")}
            </FormLabel>
            <FormControl>
              <Input
                value={tags}
                onChange={(e) => {
                  form.setValue(`nodes.${nodeIndex}.config.tags`, e.target.value, {
                    shouldDirty: true,
                  })
                  form.trigger(`nodes.${nodeIndex}.config`)
                }}
                placeholder={t(
                  "campaigns.action.availableActions.CreateGorgiasTicket.config.tagsPlaceholder",
                )}
              />
            </FormControl>
            <FormDescription>
              {t("campaigns.action.availableActions.CreateGorgiasTicket.config.tagsDescription")}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  )
}
